import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Flex,
  View,
  Image,
  Link,
  DialogContainer,
  Content,
  Accordion,
  Disclosure,
  DisclosurePanel,
  DisclosureTitle,
  ActionButton,
  Divider,
} from "@adobe/react-spectrum";
import { t } from "i18next";
import ImageAddEdit from "../formComponents/ImageAddEdit";
import styles from './ImageUploadSection.module.css';
import { Check, ExclamationTriangle, PlusCircleFill, Star, Trash } from "react-bootstrap-icons";
import { ContactFormData } from "../AddNewPerson";
import { getDownloadUrl, getImagePath, ImageVariant } from "../../../../../utils/ImagePathUtils";
import { useReefConfig } from "../../../../../hooks/UseReefConfig";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { useTranslation } from "react-i18next";

interface IImageUploadSection {
  data: ContactFormData['DOCUMENTS'];
  onChange: (data: ContactFormData['DOCUMENTS']) => void;
  treeHandle: string;
  expandedSectionKey: any;
  setExpandedSectionKey: (data: any) => void;
  validationErrors: any;
  setValidationErrors: (data: any) => void;
  fieldConfig: any;
  validationStatus: any;
  setValidationStatus: (data: any) => void;
  contactType: string;
}

const ImageUploadSection: React.FC<IImageUploadSection> = ({
  data,
  treeHandle,
  onChange,
  expandedSectionKey,
  setExpandedSectionKey,
  validationErrors,
  setValidationErrors,
  fieldConfig,
  validationStatus,
  setValidationStatus,
  contactType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSectionRequired, setIsSectionRequired] = useState(false);
  const [isImageUploaded,setIsImageUploaded] = useState(false);
  const { t } = useTranslation('contacts');
  const _tt = (key: string) => t(`contact_common.${key}`, { ns: 'contacts' });
  const handleDeleteImage = (documentId: number) => {
    const newData = (data || []).filter((doc) => doc.documentId !== documentId);
    onChange(newData);
  };

  const handleSetPrimary = (id: number) => {
    const updated = (data || []).map((img: any) => ({
      ...img,
      isPrimaryPicture: img.id === id,
    }));
    onChange(updated);
  };

  const handleConfirm = () => {
    if (!data.some((img: any) => img.isPrimaryPicture) && data.length > 0) {
      const updated = data.map((img, idx) => ({
        ...img,
        isPrimaryPicture: idx === 0,
      }));
      onChange(updated);
    }

    setExpandedSectionKey(new Set([contactType === 'p' ? 'baseInfoSection' : 'personSection']));
  };


  const buttonRef = useRef<any>(null);
    useEffect(() => {
    if (expandedSectionKey?.has?.("imageUploadSection")){
      setTimeout(() => {
        buttonRef.current?.focus();
      }, 200); 
    }
  }, [expandedSectionKey,data]);

  const handleSectionExpandedChange = () => {
    setExpandedSectionKey((prevKey: any) =>
      prevKey.has("imageUploadSection") ? new Set() : new Set(["imageUploadSection"])
    );
  };
const { baseUrl, loginInfo } = useReefConfig();
const {store} = useDependency()
  return (
    <Accordion isQuiet expandedKeys={expandedSectionKey} onExpandedChange={handleSectionExpandedChange}>
      <Disclosure id="imageUploadSection">
        <DisclosureTitle>
          <Flex justifyContent="space-between" alignItems="center" width="100%">
            <View>
              <span>{_tt(`contact_picture`)}</span>
              <span style={{ color: '#B0BEC5', fontFamily: 'Adobe Clean' }}>
                {isSectionRequired && ` (${_tt('required')})`}
              </span>
            </View>
            {validationStatus.imageUploadSection ? (
              <Check size={20} color="#1976D2" />
            ) : (
              <ExclamationTriangle size={20} color="#FF9800" />
            )}
          </Flex>
        </DisclosureTitle>
        <DisclosurePanel>
        <Divider size='M' UNSAFE_style={{marginBottom:10}}/>
          <Flex
            direction={'row'}
            alignItems={'center'}
            justifyContent={'start'}
            gap={'size-100'}
            UNSAFE_className={styles.icon_upload_parent}
          >
            <Content UNSAFE_style={{ paddingTop: 15 }}>
              <Flex direction='row' wrap gap="size-100">
                {data && data.map((image: any) => {
                const imageUrl = getDownloadUrl(image.documentId, baseUrl, store.Server, store.SessionId);
                //const imageUrlThumbnail = getImagePath(image.documentId, ImageVariant.PICTURE_CONTACT_THUMBNAIL, baseUrl, store.Server, store.SessionId);
                return(
                  <Flex key={image.id} direction='column'>
                    <Flex justifyContent='center' direction='row' gap='size-100' UNSAFE_className={styles.delete_overlay}>
                      <Link onPress={() => handleDeleteImage(image.documentId)}><Trash /></Link>
                      <Link onPress={() => handleSetPrimary(image.id)}>
                        {image.isPrimaryPicture ? (
                          <Star size={18} color="#FFD700" />
                        ) : (
                          <Star size={18} color="#B0BEC5" />
                        )}
                      </Link>
                    </Flex>
                    <Image width='size-1250' height='size-1250' src={imageUrl} UNSAFE_style={{borderRadius:'10px'}}/>
                  </Flex>
                )})}
                <View UNSAFE_className={styles.image_upload_container}>
                  <ActionButton
                    isQuiet
                    ref={buttonRef}
                    onPress={() => setIsOpen(true)}
                    UNSAFE_className={styles.icon_upload_link}
                  >
                    <PlusCircleFill size={30} color="#B0BEC5" />
                  </ActionButton>
                </View>
              </Flex>

              <DialogContainer onDismiss={() => setIsOpen(false)}>
                {isOpen && (
                  <View>
                    <ImageAddEdit
                      close={() => setIsOpen(false)}
                      images={data}
                      setImages={onChange}
                      treeHandle={treeHandle}
                    />
                  </View>
                )}
              </DialogContainer>
            </Content>
          </Flex>
          <Flex direction="row" gap={'size-100'} marginTop={10} justifyContent='end'>
            <View>
              <ActionButton onPress={handleConfirm} isQuiet UNSAFE_style={{ color: '#1976D2',padding:10 }}>
                <Check size={22} color='#1976D2' /> {_tt('button_confirm')}
              </ActionButton>
            </View>
          </Flex>
          <Divider size='M' UNSAFE_style={{marginTop:10}}/>
        </DisclosurePanel>
      </Disclosure>
    </Accordion>
  );
};

export default ImageUploadSection;
